import { useState, useEffect } from 'react';

/**
 * Material Design 3 adaptive-layout breakpoint names.
 * Maps to width thresholds: compact < 600 < medium < 840 < expanded < 1200 < large < 1600 < xlarge
 */
type Breakpoint = 'compact' | 'medium' | 'expanded' | 'large' | 'xlarge';

/** Ordered breakpoint definitions — largest first so the first match wins. */
const BREAKPOINT_QUERIES: readonly { name: Breakpoint; query: string }[] = [
  { name: 'xlarge', query: '(min-width: 1600px)' },
  { name: 'large', query: '(min-width: 1200px)' },
  { name: 'expanded', query: '(min-width: 840px)' },
  { name: 'medium', query: '(min-width: 600px)' },
] as const;

/**
 * Resolves the current breakpoint by testing media queries largest-first.
 * Falls back to `'compact'` when no query matches or when `window` is unavailable (SSR).
 */
function resolveBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') return 'compact';

  for (const { name, query } of BREAKPOINT_QUERIES) {
    if (window.matchMedia(query).matches) return name;
  }

  return 'compact';
}

/**
 * Returns the current responsive breakpoint name.
 *
 * @remarks
 * - SSR-safe — defaults to `'compact'` when `window` is not available.
 * - Subscribes to `matchMedia` change events and cleans up on unmount.
 * - Breakpoints follow MD3 adaptive layout thresholds: 600 / 840 / 1200 / 1600.
 *
 * @example
 * ```tsx
 * const bp = useBreakpoint();
 * if (bp === 'expanded' || bp === 'large' || bp === 'xlarge') {
 *   // show sidebar
 * }
 * ```
 */
function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(resolveBreakpoint);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    /** Re-evaluate on any breakpoint boundary change. */
    function handleChange(): void {
      setBreakpoint(resolveBreakpoint());
    }

    const mediaQueryLists: MediaQueryList[] = BREAKPOINT_QUERIES.map(({ query }) =>
      window.matchMedia(query),
    );

    for (const mql of mediaQueryLists) {
      mql.addEventListener('change', handleChange);
    }

    // Sync in case the value changed between initial render and effect.
    handleChange();

    return () => {
      for (const mql of mediaQueryLists) {
        mql.removeEventListener('change', handleChange);
      }
    };
  }, []);

  return breakpoint;
}

export { useBreakpoint };
export type { Breakpoint };
